// ─────────────────────────────────────────────────────────────────────────────
// AudienceScopePickerComponent
// ─────────────────────────────────────────────────────────────────────────────
// Who a price is for, asked once and the same way everywhere.
//
// The knowledge base has asked this since documents existed: a select with
// three answers, chips for the named ones. Grown per screen instead, the
// pricing dialogs ended up with two vocabularies, one of which could not
// express what the server already stored.
//
// It takes the options it should offer rather than hard-coding them: the import
// step has a fourth answer the edit dialog does not ("the file says who"), and
// the edit dialog looks at one row with no file left to ask.
//
// Customers stay single, groups multiple: on both models the customer is a
// Many2one and the groups a Many2many, so a multi-customer selection would mean
// N records or a model change.
// ─────────────────────────────────────────────────────────────────────────────

import { CommonModule } from '@angular/common';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { CompaniesGroupsService } from 'src/app/features/companies-groups/services/companies-groups.service';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { PriceImportAudience } from 'src/app/features/knowledge-base/models/price-observation.models';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { UiModule } from 'src/app/ui/ui.module';

/**
 * The audiences a price can be published to — the models' own union, named
 * here for what the picker asks rather than for who stores it.
 */
export type PriceAudience = PriceImportAudience;

/** A picked customer: the id the payload travels with, the name the chip shows. */
export interface ICustomerChoice {
  id: number;
  name: string;
}

export interface IAudienceScope {
  audience: PriceAudience | null;
  /**
   * The customers this audience names -- a list, like the knowledge base's own
   * upload.
   *
   * A price row is still for one customer, and an import naming three writes
   * three rows per line. A host that accepts only one sets `maxCustomers` to 1
   * and reads the head.
   */
  customers: ICustomerChoice[];
  groupIds: number[];
}

export const EMPTY_AUDIENCE_SCOPE: IAudienceScope = {
  audience: null,
  customers: [],
  groupIds: [],
};

/**
 * A named audience with nobody named -- the one shape the engine can never
 * serve, since every lookup asks for a customer or that customer's groups.
 *
 * Whether an unanswered audience (`null`) is itself a refusal is the host's
 * question: the import must have one, an existing row may carry none.
 */
export function isAudienceIncomplete(scope: IAudienceScope): boolean {
  if (scope.audience === 'customer') return scope.customers.length === 0;
  if (scope.audience === 'group') return scope.groupIds.length === 0;
  return false;
}

/** Short enough to be responsive, long enough not to search on one letter. */
const MIN_SEARCH_LENGTH = 2;

/** The KB's own wording for the same three answers — not a second vocabulary. */
const LABEL_KEY: Record<PriceAudience, string> = {
  file: 'knowledge_base.import.scope.from_file',
  global: 'knowledge_base.upload.audience_all',
  customer: 'knowledge_base.upload.audience_by_company',
  group: 'knowledge_base.upload.audience_by_group',
};

@Component({
  selector: 'app-audience-scope-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatAutocompleteModule,
    MatChipsModule,
    UiModule,
  ],
  templateUrl: './audience-scope-picker.component.html',
  styleUrls: ['./audience-scope-picker.component.scss'],
})
export class AudienceScopePickerComponent implements OnInit, OnDestroy {
  /** Which answers to offer, in the order they should read. */
  @Input() set options(value: PriceAudience[]) {
    this._options = value;
    this.refreshOptionLabels();
  }
  get options(): PriceAudience[] {
    return this._options;
  }
  @Input() value: IAudienceScope = { ...EMPTY_AUDIENCE_SCOPE };
  /** Rendered under the select when the chosen answer has one. */
  @Input() hintKey: string | null = null;
  /**
   * The question above the select. Overridable because a host that asks it in
   * its own words ("These prices apply to") used to put a heading of its own
   * ABOVE the picker, which then added this one underneath -- two labels
   * stacked on one field.
   */
  @Input() labelKey = 'knowledge_base.upload.select_audience';
  /**
   * Per-audience wording, when the default names the wrong thing.
   *
   * "The file says who" is right for a spreadsheet and wrong for a message,
   * where the thread's counterparty is read instead. The host that knows which
   * it is passes the key rather than this component guessing.
   */
  @Input() set labelKeys(overrides: Partial<Record<PriceAudience, string>>) {
    this._labelKeys = overrides ?? {};
    this.refreshOptionLabels();
  }
  /**
   * How many customers this host can accept. 1 for a screen looking at a
   * single price row; unbounded for the upload, which publishes the same file
   * to several accounts.
   */
  @Input() maxCustomers = 0;
  @Output() valueChange = new EventEmitter<IAudienceScope>();

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  /**
   * The offered answers, already translated.
   *
   * `ui-form-field` renders `label` verbatim, so the key is resolved here and
   * once per `options` write: bound to a getter it re-runs `instant()` and
   * hands `*ngFor` a new array every tick.
   */
  translatedOptions: SelectOption[] = [];

  private _options: PriceAudience[] = ['global', 'customer', 'group'];
  private _labelKeys: Partial<Record<PriceAudience, string>> = {};

  audienceControl = new FormControl<PriceAudience | null>(null);
  customerControl = new FormControl('');
  groupControl = new FormControl('');

  customerOptions$!: Observable<SelectOption[]>;
  filteredGroups$!: Observable<ICompaniesGroups[]>;

  /** The customer chips — the ONE owner of who is selected, like the groups. */
  get selectedCustomers(): ICustomerChoice[] {
    return this.value.customers;
  }

  /**
   * The chips, and the one owner of what is selected.
   *
   * `value.groupIds` is derived from it, never the reverse: with the
   * autocomplete pipe also writing this field, adding a chip clears the input,
   * the pipe re-runs against the not-yet-emitted value and the chip vanishes in
   * the same tick.
   */
  selectedGroups: ICompaniesGroups[] = [];

  /** The full group list, fetched once: the chips filter it, they do not search. */
  private groups: ICompaniesGroups[] = [];
  private groups$ = new ReplaySubject<ICompaniesGroups[]>(1);

  private destroy$ = new Subject<void>();
  /** A chip was added or removed: the customer list must re-filter itself. */
  private selectionChanged$ = new Subject<void>();

  constructor(
    private customersService: CustomersService,
    private companiesGroupsService: CompaniesGroupsService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.refreshOptionLabels();
    this.audienceControl.setValue(this.value.audience ?? null, { emitEvent: false });
    this.customerControl.setValue('', { emitEvent: false });

    this.audienceControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((audience) => this.onAudienceChanged(audience));

    // A LIST, offered before a single key is pressed. Searching only from the
    // second character meant the field answered nothing until the operator
    // already knew the name they were looking for — the groups beside it have
    // always shown theirs. Typing still narrows it, server-side.
    const searched$: Observable<SelectOption[]> = this.customerControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term.trim() : '';
        return this.customersService
          .listCustomers(
            { number: 1, size: 20 },
            undefined,
            keyword.length >= MIN_SEARCH_LENGTH ? keyword : undefined,
          )
          .pipe(
            map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name }))),
            // A failed lookup empties the list rather than breaking the
            // dialog: every other answer stays reachable.
            catchError(() => of([] as SelectOption[])),
          );
      }),
    );
    // Re-filtered on every pick and every removal WITHOUT a new search: the
    // filter below used to sit inside the search pipeline, so it only re-ran
    // when the TERM changed -- and `removeCustomer` "refreshed" it by setting
    // an already-empty control to '', an emission `distinctUntilChanged`
    // swallowed. A removed customer stayed absent from the list until two
    // characters were typed.
    this.customerOptions$ = combineLatest([
      searched$,
      this.selectionChanged$.pipe(startWith(undefined)),
    ]).pipe(
      map(([options]) => {
        // Already-chipped customers drop out of the list rather than being
        // offered twice.
        const picked = new Set(this.selectedCustomers.map((customer) => customer.id));
        return options.filter((option) => !picked.has(Number(option.value)));
      }),
      takeUntil(this.destroy$),
    );

    // Groups are a short, stable list — one fetch, then a local filter. A
    // paged search here would be a second answer to "which groups exist".
    //
    // Subscribed HERE and not through the template's async pipe: the arrival
    // of the list turns the incoming `groupIds` into chips, and a component
    // field written while the view is being checked is the
    // ExpressionChangedAfterItHasBeenChecked this used to throw.
    this.companiesGroupsService
      .listCompaniesGroups()
      .pipe(
        map((res) => (res.data ?? []).filter((g) => g.name?.trim())),
        takeUntil(this.destroy$),
      )
      .subscribe((groups) => {
        this.groups = groups;
        // Ids in, objects out — once, and only for what has not been picked
        // by hand since: the operator's own choices outrank a late fetch.
        if (!this.selectedGroups.length) {
          this.selectedGroups = groups.filter((g) => this.value.groupIds.includes(g.id));
        }
        this.groups$.next(groups);
      });

    // A PURE derivation: it reads the selection, it never writes it.
    this.filteredGroups$ = combineLatest([
      this.groupControl.valueChanges.pipe(startWith('')),
      this.groups$.pipe(startWith([] as ICompaniesGroups[])),
    ]).pipe(
      map(([term, groups]) => {
        const picked = new Set(this.selectedGroups.map((g) => g.id));
        const keyword = (typeof term === 'string' ? term : '').toLowerCase();
        return groups.filter(
          (g) => !picked.has(g.id) && (!keyword || (g.name ?? '').toLowerCase().includes(keyword)),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private refreshOptionLabels(): void {
    this.translatedOptions = this._options.map((audience) => ({
      value: audience,
      label: this.translateService.instant(
        this._labelKeys[audience] ?? LABEL_KEY[audience],
      ),
    }));
  }

  get isCustomer(): boolean {
    return this.value.audience === 'customer';
  }

  get isGroup(): boolean {
    return this.value.audience === 'group';
  }

  /** The audience is chosen but nobody is named — SAVE/CONTINUE must wait. */
  get isIncomplete(): boolean {
    return this.value.audience == null || isAudienceIncomplete(this.value);
  }

  displayCustomer(option: SelectOption | string): string {
    return typeof option === 'string' ? option : (option?.label ?? '');
  }

  // ── Edits ─────────────────────────────────────────────────────────────────

  /**
   * Leaving an audience DROPS what it named.
   *
   * A customer left behind by a switch to "All customers" would keep
   * travelling in the payload while the field that shows it is gone — an
   * invisible value is the one nobody can correct.
   */
  private onAudienceChanged(audience: PriceAudience | null): void {
    const next: IAudienceScope = { ...this.value, audience };
    if (audience !== 'customer') {
      next.customers = [];
      this.customerControl.setValue('', { emitEvent: false });
    }
    if (audience !== 'group') {
      next.groupIds = [];
      this.selectedGroups = [];
      this.groupControl.setValue('', { emitEvent: false });
    }
    this.emit(next);
  }

  onCustomerSelected(option: SelectOption): void {
    const picked: ICustomerChoice = { id: Number(option.value), name: option.label };
    if (this.selectedCustomers.some((customer) => customer.id === picked.id)) return;
    const customers = this.maxCustomers === 1
      ? [picked]
      : [...this.selectedCustomers, picked];
    this.emit({ ...this.value, audience: 'customer', customers });
    this.selectionChanged$.next();
    this.customerControl.setValue('');
  }

  removeCustomer(customer: ICustomerChoice): void {
    this.emit({
      ...this.value,
      customers: this.selectedCustomers.filter((picked) => picked.id !== customer.id),
    });
    // An explicit re-offer, not a `setValue('')` that depends on the value
    // having changed.
    this.selectionChanged$.next();
  }

  /** One host accepts one customer, and it already has it. */
  get customerLimitReached(): boolean {
    return this.maxCustomers > 0 && this.selectedCustomers.length >= this.maxCustomers;
  }

  addGroup(group: ICompaniesGroups): void {
    if (this.selectedGroups.some((picked) => picked.id === group.id)) return;
    this.selectedGroups = [...this.selectedGroups, group];
    this.emitGroups();
  }

  removeGroup(group: ICompaniesGroups): void {
    this.selectedGroups = this.selectedGroups.filter((g) => g.id !== group.id);
    this.emitGroups();
  }

  /** The chips are the truth; `groupIds` follows, and the input is cleared. */
  private emitGroups(): void {
    this.emit({ ...this.value, groupIds: this.selectedGroups.map((g) => g.id) });
    this.groupControl.setValue('');
  }

  get allGroupsPicked(): boolean {
    return this.groups.length > 0 && this.selectedGroups.length === this.groups.length;
  }

  private emit(next: IAudienceScope): void {
    this.value = next;
    this.valueChange.emit(next);
  }
}
